import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { client, redeemPromoCode } from '../api/client';
import { useI18n } from '../i18n';
import { useToast } from '../components/common/Toast';

// Tip za paket tokena
interface TokenPackage {
  id: string;
  tokens: number;
  price: number;
  currency: string;
  popular: boolean;
  is_active: boolean;
}

// Tip za stavku u korpi
interface CartItem {
  packageId: string;
  quantity: number;
}

// Rezultat greedy algoritma
interface CartBreakdown {
  packages: { pkg: TokenPackage; quantity: number }[];
  individualTokens: number;
  totalTokens: number;
  totalPrice: number;
}

// Cena pojedinačnog tokena (RSD)
const INDIVIDUAL_TOKEN_PRICE = 100;

export function BuyTokensPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, tokenBalance, setTokenBalance } = useAppStore();
  const { language } = useI18n();
  const toast = useToast();
  
  // Cart state - količine po paketima
  const [cart, setCart] = useState<Record<string, number>>({});
  // Custom input za broj tokena
  const [customTokenInput, setCustomTokenInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [tokenPackages, setTokenPackages] = useState<TokenPackage[]>([]);
  const [promoCode, setPromoCode] = useState('');
  const [redeemStatus, setRedeemStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Učitaj tokene iz baze prilikom mount-a
  useEffect(() => {
    if (isAuthenticated) {
      loadUserTokens();
    }
    loadTokenPackages();
  }, [isAuthenticated]);

  const loadTokenPackages = async () => {
    try {
      const res = await client.api.fetch('/api/pricing/token-packages');
      if (res.ok) {
        const data = await res.json();
        if (data.data && Array.isArray(data.data)) {
          // Transformacija podataka za frontend kompatibilnost
          const transformedPackages = data.data.map((pkg: any) => ({
            id: String(pkg.id),
            tokens: Number(pkg.tokens),
            price: Number(pkg.price || pkg.price_rsd),
            currency: pkg.currency || 'RSD',
            popular: Boolean(pkg.popular || pkg.is_popular),
            is_active: Boolean(pkg.is_active) || pkg.is_active === 1 || pkg.is_active === '1'
          }));
          setTokenPackages(transformedPackages);
        }
      } else {
        throw new Error('API error');
      }
    } catch (err) {
      console.error('Failed to load token packages:', err);
      // Fallback to defaults
      setTokenPackages([
        { id: '1', tokens: 5, price: 480, currency: 'RSD', popular: false, is_active: true },
        { id: '2', tokens: 10, price: 900, currency: 'RSD', popular: true, is_active: true },
        { id: '3', tokens: 20, price: 1700, currency: 'RSD', popular: false, is_active: true },
        { id: '4', tokens: 30, price: 2500, currency: 'RSD', popular: false, is_active: true },
      ]);
    } finally {
      setLoadingPackages(false);
    }
  };

  const loadUserTokens = async () => {
    try {
      const res = await client.api.fetch('/api/tokens');
      const data = await res.json();
      if (data.data?.balance !== undefined) {
        setTokenBalance(data.data.balance);
      }
    } catch (err) {
      console.error('Failed to load tokens:', err);
    } finally {
      setLoadingTokens(false);
    }
  };

  // Greedy algoritam - oduzima najveći paket od preostalog iznosa
  const calculateOptimalPackages = (totalTokens: number, packages: TokenPackage[]): CartBreakdown => {
    // Sortiraj pakete od najvećeg ka najmanjem
    const sortedPackages = [...packages]
      .filter(p => p.is_active)
      .sort((a, b) => b.tokens - a.tokens);
    
    const result: { pkg: TokenPackage; quantity: number }[] = [];
    let remaining = totalTokens;
    
    // Greedy: oduzimaj najveći paket koji stane
    for (const pkg of sortedPackages) {
      if (remaining >= pkg.tokens) {
        const count = Math.floor(remaining / pkg.tokens);
        if (count > 0) {
          result.push({ pkg, quantity: count });
          remaining -= count * pkg.tokens;
        }
      }
    }
    
    // Preostali tokeni se kupuju pojedinačno po 100 RSD
    const individualTokens = remaining;
    
    // Izračunaj ukupnu cenu
    const packagesTotal = result.reduce((sum, item) => sum + (item.pkg.price * item.quantity), 0);
    const individualTotal = individualTokens * INDIVIDUAL_TOKEN_PRICE;
    
    return {
      packages: result,
      individualTokens,
      totalTokens,
      totalPrice: packagesTotal + individualTotal
    };
  };

  // Funkcije za upravljanje korpom
  const addToCart = (packageId: string) => {
    setCart(prev => ({
      ...prev,
      [packageId]: (prev[packageId] || 0) + 1
    }));
    setShowCustomInput(false);
    setCustomTokenInput('');
  };

  const removeFromCart = (packageId: string) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[packageId] > 1) {
        newCart[packageId]--;
      } else {
        delete newCart[packageId];
      }
      return newCart;
    });
  };

  const clearCart = () => {
    setCart({});
    setCustomTokenInput('');
    setShowCustomInput(false);
  };

  // Izračunaj sadržaj korpe na osnovu ručnog izbora paketa
  const manualCartBreakdown = useMemo((): CartBreakdown | null => {
    const cartEntries = Object.entries(cart);
    if (cartEntries.length === 0) return null;
    
    const packages: { pkg: TokenPackage; quantity: number }[] = [];
    let totalTokens = 0;
    let totalPrice = 0;
    
    for (const [pkgId, qty] of cartEntries) {
      const pkg = tokenPackages.find(p => p.id === pkgId);
      if (pkg && qty > 0) {
        packages.push({ pkg, quantity: qty });
        totalTokens += pkg.tokens * qty;
        totalPrice += pkg.price * qty;
      }
    }
    
    return {
      packages,
      individualTokens: 0,
      totalTokens,
      totalPrice
    };
  }, [cart, tokenPackages]);

  // Izračunaj optimalni raspored za custom input
  const customCartBreakdown = useMemo((): CartBreakdown | null => {
    const tokens = parseInt(customTokenInput);
    if (isNaN(tokens) || tokens <= 0) return null;
    return calculateOptimalPackages(tokens, tokenPackages);
  }, [customTokenInput, tokenPackages]);

  // Aktivni breakdown (custom ima prioritet ako je unet)
  const activeBreakdown = showCustomInput && customCartBreakdown ? customCartBreakdown : manualCartBreakdown;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8"
          >
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Potrebna prijava' : 'Login Required'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {language === 'sr' ? 'Za kupovinu tokena morate biti prijavljeni.' : 'You must be logged in to buy tokens.'}
            </p>
            <button
              onClick={() => navigate('/prijava')}
              className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
            >
              {language === 'sr' ? 'Prijavi se' : 'Login'}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Provera email verifikacije
  if (!user?.emailVerified) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8"
          >
            <svg className="w-16 h-16 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Potrebna verifikacija email-a' : 'Email Verification Required'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {language === 'sr' 
                ? 'Za kupovinu tokena morate prvo verifikovati vašu email adresu. Proverite vaš inbox za verifikacioni link.' 
                : 'To buy tokens, you must first verify your email address. Check your inbox for the verification link.'}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/profil')}
                className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Idi na profil' : 'Go to Profile'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                {language === 'sr' ? 'Početna' : 'Home'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const handleRedeem = async () => {
    if (!promoCode.trim()) return;
    
    try {
      const result = await redeemPromoCode(promoCode.trim());
      if (result.success) {
        setRedeemStatus('success');
        setPromoCode('');
        // Reload tokens to get updated balance
        await loadUserTokens();
        toast.success(
          language === 'sr' ? 'Kod aktiviran' : 'Code redeemed',
          language === 'sr' ? 'Promo kod uspešno aktiviran!' : 'Promo code successfully redeemed!'
        );
        setTimeout(() => setRedeemStatus('idle'), 3000);
      } else {
        setRedeemStatus('error');
        toast.error(
          language === 'sr' ? 'Nevažeći kod' : 'Invalid code',
          result.error || (language === 'sr' ? 'Nevažeći promo kod' : 'Invalid promo code')
        );
        setTimeout(() => setRedeemStatus('idle'), 3000);
      }
    } catch (error) {
      console.error('Error redeeming promo code:', error);
      setRedeemStatus('error');
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri aktivaciji promo koda. Pokušajte ponovo.' : 'Error redeeming promo code. Please try again.'
      );
      setTimeout(() => setRedeemStatus('idle'), 3000);
    }
  };

  const handlePurchase = async () => {
    if (!activeBreakdown) return;

    setIsProcessing(true);
    try {
      // Pripremi opis kupovine
      const packageDescriptions = activeBreakdown.packages
        .map(item => `${item.quantity}x ${item.pkg.tokens} tokena`)
        .join(' + ');
      const individualDesc = activeBreakdown.individualTokens > 0 
        ? ` + ${activeBreakdown.individualTokens} pojedinačnih tokena`
        : '';
      const description = packageDescriptions + individualDesc;

      // Pozovi backend API za inicijalizaciju plaćanja
      const response = await client.api.fetch('/api/payment/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: activeBreakdown.totalPrice, // Ukupna cena u RSD
          tokens: activeBreakdown.totalTokens,
          description: description,
          breakdown: {
            packages: activeBreakdown.packages.map(item => ({
              packageId: item.pkg.id,
              tokens: item.pkg.tokens,
              price: item.pkg.price,
              quantity: item.quantity
            })),
            individualTokens: activeBreakdown.individualTokens,
            individualTokenPrice: INDIVIDUAL_TOKEN_PRICE
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Redirect to payment gateway using hidden form submission
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.gatewayUrl;
        
        // Add all form fields from backend
        Object.entries(data.formData as Record<string, string>).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        
        document.body.appendChild(form);
        form.submit();
        
        // Don't clear cart or show success - user will be redirected
      } else {
        const error = await response.json();
        toast.error(
          language === 'sr' ? 'Greška' : 'Error',
          error.error || (language === 'sr' ? 'Inicijalizacija plaćanja nije uspela' : 'Payment initialization failed')
        );
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri pokretanju plaćanja. Molimo pokušajte ponovo.' : 'Error initiating payment. Please try again.'
      );
      setIsProcessing(false);
    }
    // Note: setIsProcessing(false) is not called on success because page will redirect
  };

  // Back button component
  const BackButton = () => (
    <button
      onClick={() => navigate(-1)}
      className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span>{language === 'sr' ? 'Nazad' : 'Back'}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back Button */}
          <BackButton />

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {language === 'sr' ? 'Kupi tokene' : 'Buy Tokens'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {language === 'sr' ? 'Tokeni su potrebni za objavljivanje oglasa na platformi.' : 'Tokens are needed for posting listings on the platform.'}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 rounded-full">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
              </svg>
              <span className="font-medium">
                {language === 'sr' ? 'Trenutni balans:' : 'Current balance:'} {loadingTokens ? '...' : tokenBalance} {language === 'sr' ? 'tokena' : 'tokens'}
              </span>
            </div>
          </div>

          {/* Packages */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {loadingPackages ? (
              <div className="col-span-4 text-center text-slate-500 dark:text-slate-400 py-8">
                {language === 'sr' ? 'Učitavanje paketa...' : 'Loading packages...'}
              </div>
            ) : (
              tokenPackages.filter(p => p.is_active).map((pkg) => {
                const quantity = cart[pkg.id] || 0;
                const isInCart = quantity > 0;
                
                return (
                  <motion.div
                    key={pkg.id}
                    whileHover={{ scale: 1.02 }}
                    className={`relative p-4 sm:p-6 rounded-xl border-2 transition-colors ${
                      isInCart
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-amber-300'
                    }`}
                  >
                    {pkg.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-xs font-semibold text-slate-900 rounded-full">
                        {language === 'sr' ? 'POPULARNO' : 'POPULAR'}
                      </span>
                    )}
                    
                    {/* Količina badge */}
                    {isInCart && (
                      <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-slate-900 text-sm font-bold rounded-full flex items-center justify-center">
                        {quantity}
                      </span>
                    )}
                    
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-1">{pkg.tokens}</div>
                    <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-2 sm:mb-3">{language === 'sr' ? 'tokena' : 'tokens'}</div>
                    <div className="text-lg sm:text-xl font-semibold text-amber-600 mb-1">
                      {pkg.price.toLocaleString()} {pkg.currency}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                      {(pkg.price / pkg.tokens).toFixed(0)} {pkg.currency}/{language === 'sr' ? 'token' : 'token'}
                    </div>
                    
                    {/* +/- dugmad */}
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => removeFromCart(pkg.id)}
                        disabled={!isInCart}
                        className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="w-8 text-center font-semibold text-slate-900 dark:text-white">{quantity}</span>
                      <button
                        onClick={() => addToCart(pkg.id)}
                        className="w-8 h-8 rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400 transition-colors flex items-center justify-center"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Custom Token Input */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 dark:text-white">
                {language === 'sr' ? 'Ili unesite željeni broj tokena' : 'Or enter custom token amount'}
              </h3>
              <button
                onClick={() => {
                  setShowCustomInput(!showCustomInput);
                  if (!showCustomInput) {
                    setCart({}); // Očisti ručno dodato
                  } else {
                    setCustomTokenInput('');
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showCustomInput 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                }`}
              >
                {showCustomInput 
                  ? (language === 'sr' ? 'Nazad na pakete' : 'Back to packages') 
                  : (language === 'sr' ? 'Unesite broj' : 'Enter amount')}
              </button>
            </div>
            
            <AnimatePresence>
              {showCustomInput && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex gap-4 items-center">
                    <input
                      type="number"
                      min="1"
                      value={customTokenInput}
                      onChange={(e) => setCustomTokenInput(e.target.value)}
                      placeholder={language === 'sr' ? 'Npr. 37 tokena' : 'E.g., 37 tokens'}
                      className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-lg"
                    />
                    <span className="text-slate-600 dark:text-slate-400 font-medium">
                      {language === 'sr' ? 'tokena' : 'tokens'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {language === 'sr' 
                      ? 'Sistem će automatski izračunati najpovoljniju kombinaciju paketa. Pojedinačni tokeni koštaju 100 RSD.'
                      : 'The system will automatically calculate the best package combination. Individual tokens cost 100 RSD.'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Cart Summary */}
          <AnimatePresence>
            {activeBreakdown && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {language === 'sr' ? 'Vaša korpa' : 'Your cart'}
                  </h3>
                  <button
                    onClick={clearCart}
                    className="text-sm text-slate-500 hover:text-red-500 transition-colors"
                  >
                    {language === 'sr' ? 'Isprazni' : 'Clear'}
                  </button>
                </div>

                {/* Breakdown */}
                <div className="space-y-3 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                  {activeBreakdown.packages.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                      <span>
                        {item.quantity}x {language === 'sr' ? 'Paket' : 'Package'} {item.pkg.tokens} {language === 'sr' ? 'tokena' : 'tokens'}
                      </span>
                      <span className="font-medium">
                        {(item.pkg.price * item.quantity).toLocaleString()} RSD
                      </span>
                    </div>
                  ))}
                  {activeBreakdown.individualTokens > 0 && (
                    <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                      <span>
                        {activeBreakdown.individualTokens}x {language === 'sr' ? 'Pojedinačni token' : 'Individual token'} ({INDIVIDUAL_TOKEN_PRICE} RSD)
                      </span>
                      <span className="font-medium">
                        {(activeBreakdown.individualTokens * INDIVIDUAL_TOKEN_PRICE).toLocaleString()} RSD
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Ukupno tokena' : 'Total tokens'}
                    </div>
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">
                      {activeBreakdown.totalTokens} {language === 'sr' ? 'tokena' : 'tokens'}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Ukupna cena' : 'Total price'}
                    </div>
                    <span className="text-2xl font-bold text-amber-600">
                      {activeBreakdown.totalPrice.toLocaleString()} RSD
                    </span>
                  </div>
                </div>

                {/* Ušteda info za custom input */}
                {showCustomInput && customCartBreakdown && customCartBreakdown.totalTokens > 0 && (
                  <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-400">
                      💡 {language === 'sr' 
                        ? `Uštedeli ste ${(customCartBreakdown.totalTokens * INDIVIDUAL_TOKEN_PRICE - customCartBreakdown.totalPrice).toLocaleString()} RSD u odnosu na kupovinu pojedinačnih tokena!`
                        : `You saved ${(customCartBreakdown.totalTokens * INDIVIDUAL_TOKEN_PRICE - customCartBreakdown.totalPrice).toLocaleString()} RSD compared to buying individual tokens!`}
                    </p>
                  </div>
                )}

                <button
                  onClick={handlePurchase}
                  disabled={isProcessing}
                  className="w-full py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {language === 'sr' ? 'Obrađujem...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {language === 'sr' ? 'Kupi tokene' : 'Buy tokens'}
                    </>
                  )}
                </button>

              </motion.div>
            )}
          </AnimatePresence>

          {/* Pricing Info */}
          <div className="mt-8 bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {language === 'sr' ? 'Cenovnik oglašavanja' : 'Listing Pricing'}
            </h3>
            
            {/* New Listing Pricing */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-3">
                {language === 'sr' ? 'Novi oglas (Ponude i Potražnje)' : 'New Listing (Offers & Demands)'}
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center border-2 border-transparent hover:border-amber-500 transition-colors">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">1</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{language === 'sr' ? 'token' : 'token'}</div>
                  <div className="font-semibold text-slate-900 dark:text-white mt-2">7 {language === 'sr' ? 'dana' : 'days'}</div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center border-2 border-transparent hover:border-amber-500 transition-colors">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">3</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{language === 'sr' ? 'tokena' : 'tokens'}</div>
                  <div className="font-semibold text-slate-900 dark:text-white mt-2">15 {language === 'sr' ? 'dana' : 'days'}</div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-center border-2 border-transparent hover:border-amber-500 transition-colors">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">5</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{language === 'sr' ? 'tokena' : 'tokens'}</div>
                  <div className="font-semibold text-slate-900 dark:text-white mt-2">30 {language === 'sr' ? 'dana' : 'days'}</div>
                </div>
              </div>
            </div>
            
            {/* Extension Pricing */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-3">
                {language === 'sr' ? 'Produženje oglasa' : 'Listing Extension'}
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center border-2 border-transparent hover:border-green-500 transition-colors">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">1</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{language === 'sr' ? 'token' : 'token'}</div>
                  <div className="font-semibold text-slate-900 dark:text-white mt-2">+7 {language === 'sr' ? 'dana' : 'days'}</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center border-2 border-transparent hover:border-green-500 transition-colors">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">2</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{language === 'sr' ? 'tokena' : 'tokens'}</div>
                  <div className="font-semibold text-slate-900 dark:text-white mt-2">+15 {language === 'sr' ? 'dana' : 'days'}</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center border-2 border-transparent hover:border-green-500 transition-colors">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">3</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{language === 'sr' ? 'tokena' : 'tokens'}</div>
                  <div className="font-semibold text-slate-900 dark:text-white mt-2">+30 {language === 'sr' ? 'dana' : 'days'}</div>
                </div>
              </div>
            </div>
            
            {/* Free Tokens on Registration */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-900 dark:text-amber-200">
                    {language === 'sr' ? 'Besplatni tokeni pri registraciji!' : 'Free tokens on registration!'}
                  </h4>
                  <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                    {language === 'sr' 
                      ? 'Novi korisnici dobijaju besplatne tokene nakon verifikacije email adrese: fizička lica dobijaju 5 tokena, a pravna lica čak 20 tokena!' 
                      : 'New users receive free tokens after email verification: individuals get 5 tokens, companies get 20 tokens!'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Token info note */}
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 text-center">
              {language === 'sr' 
                ? 'Tokeni nikada ne ističu dok ih ne iskoristite. Veći paketi imaju povoljniju cenu po tokenu.'
                : 'Tokens never expire until you use them. Larger packages have a better price per token.'}
            </p>
          </div>

          {/* Redeem Code */}
          <div className="mt-8 bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Imate promo kod?' : 'Have a promo code?'}
            </h3>
            <div className="flex gap-4">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder={language === 'sr' ? 'Unesite kod' : 'Enter code'}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button
                onClick={handleRedeem}
                disabled={!promoCode.trim() || redeemStatus !== 'idle'}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  redeemStatus === 'success'
                    ? 'bg-green-500 text-white'
                    : redeemStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-slate-900 dark:bg-slate-700 text-white hover:bg-slate-800 dark:hover:bg-slate-600'
                }`}
              >
                {redeemStatus === 'success'
                  ? (language === 'sr' ? 'Uspešno!' : 'Success!')
                  : redeemStatus === 'error'
                  ? (language === 'sr' ? 'Nevažeći kod' : 'Invalid code')
                  : (language === 'sr' ? 'Aktiviraj' : 'Redeem')}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
